import { Observable } from 'rxjs/Observable'
import { combineEpics } from 'redux-observable'

import app from 'containers/app'
import { api } from './api'
import * as actions from './actions'
import {
  fetchAssistantOrgSettingsSuccess,
  fetchAssistantAgentSettingsSuccess,
  fetchSalesforceSettingsSuccess,
  fetchPersonalizationTagsSuccess,
} from './selectors'

// Org settings epics
const fetchAssistantOrgSettingsEpic = (action$, { getState }) =>
  action$
    .ofType(actions.fetchAssistantOrgSettings.toString())
    .pluck('payload')
    .filter(() => !fetchAssistantOrgSettingsSuccess(getState()))
    .mergeMap(() => {
      return api
        .fetchAssistantOrgSettings()
        .map(response => actions.fetchAssistantOrgSettingsSuccess(response))
        .startWith(actions.fetchAssistantOrgSettingsPending())
        .catch(error => Observable.of(actions.fetchAssistantOrgSettingsFailed(error)))
    })

const createAssistantOrgSettingsEpic = action$ =>
  action$
    .ofType(actions.createAssistantOrgSettings.toString())
    .map(action => action.payload)
    .mergeMap(assistantOrgSettings =>
      api
        .createAssistantOrgSettings(assistantOrgSettings)
        .map(response => {
          app.actions.NotificationActions.pushNotification({
            content: "Your organization's Drift Assistant settings have been updated.",
            variant: 'green',
          })
          return actions.createAssistantOrgSettingsSuccess(response)
        })
        .startWith(actions.createAssistantOrgSettingsPending())
        .catch(error => {
          app.actions.NotificationActions.pushNotification({
            content: "We were unable to update your organization's Drift Assistant settings.",
            variant: 'red',
            sticky: true,
          })
          return Observable.of(actions.createAssistantOrgSettingsFailed(error))
        })
    )

const patchAssistantOrgSettingsEpic = (action$, { dispatch }) =>
  action$
    .ofType(actions.patchAssistantOrgSettings.toString())
    .map(action => action.payload)
    .mergeMap(patchOrgSettingsPayload =>
      api
        .patchAssistantOrgSettings(patchOrgSettingsPayload.newSettings)
        .map(response => {
          app.actions.NotificationActions.pushNotification({
            content: "Your organization's Drift Assistant settings have been updated.",
            variant: 'green',
          })
          dispatch(
            actions.handleEditConfig({ settingName: patchOrgSettingsPayload.settingName, value: false })
          )
          return actions.patchAssistantOrgSettingsSuccess(response)
        })
        .startWith(actions.patchAssistantOrgSettingsPending())
        .catch(error => {
          app.actions.NotificationActions.pushNotification({
            content: "We were unable to update your organization's Drift Assistant settings.",
            variant: 'red',
            sticky: true,
          })
          return Observable.of(actions.patchAssistantOrgSettingsFailed(error))
        })
    )

// Agent settings epics
const fetchAssistantAgentSettingsEpic = (action$, { getState }) =>
  action$
    .ofType(actions.fetchAssistantAgentSettings.toString())
    .pluck('payload')
    .filter(() => !fetchAssistantAgentSettingsSuccess(getState()))
    .mergeMap(() => {
      return api
        .fetchAssistantAgentSettings()
        .map(response => actions.fetchAssistantAgentSettingsSuccess(response))
        .startWith(actions.fetchAssistantAgentSettingsPending())
        .catch(error => Observable.of(actions.fetchAssistantAgentSettingsFailed(error)))
    })

const createAssistantAgentSettingsEpic = action$ =>
  action$
    .ofType(actions.createAssistantAgentSettings.toString())
    .map(action => action.payload)
    .mergeMap(assistantAgentSettings =>
      api
        .createAssistantAgentSettings(assistantAgentSettings)
        .map(response => {
          app.actions.NotificationActions.pushNotification({
            content: 'Your Drift Assistant settings have been updated.',
            variant: 'green',
          })
          return actions.createAssistantAgentSettingsSuccess(response)
        })
        .startWith(actions.createAssistantAgentSettingsPending())
        .catch(error => {
          app.actions.NotificationActions.pushNotification({
            content: 'We were unable to update your Drift Assistant settings.',
            variant: 'red',
            sticky: true,
          })
          return Observable.of(actions.createAssistantAgentSettingsFailed(error))
        })
    )

const patchAssistantAgentSettingsEpic = action$ =>
  action$
    .ofType(actions.patchAssistantAgentSettings.toString())
    .map(action => action.payload)
    .mergeMap(({ settings, meetingCopy }) => {
      const toastCopy = !!meetingCopy ? 'meeting' : 'Drift Assistant'
      return api
        .patchAssistantAgentSettings({ taskConfigMap: settings })
        .map(response => {
          app.actions.NotificationActions.pushNotification({
            content: `Your ${toastCopy} settings have been updated.`,
            variant: 'green',
          })
          return actions.patchAssistantAgentSettingsSuccess(response)
        })
        .startWith(actions.patchAssistantAgentSettingsPending())
        .catch(error => {
          app.actions.NotificationActions.pushNotification({
            content: `We were unable to update your ${toastCopy} settings.`,
            variant: 'red',
            sticky: true,
          })
          return Observable.of(actions.patchAssistantAgentSettingsFailed(error))
        })
    })

const fetchSalesforceSettingsEpic = (action$, { getState }) =>
  action$
    .ofType(actions.fetchSalesforceSettings.toString())
    .pluck('payload')
    .filter(() => !fetchSalesforceSettingsSuccess(getState()))
    .mergeMap(() => {
      return api
        .fetchSalesforceSettings()
        .map(response => actions.fetchSalesforceSettingsSuccess(response))
        .startWith(actions.fetchSalesforceSettingsPending())
        .catch(error => Observable.of(actions.fetchSalesforceSettingsFailed(error)))
    })

const fetchPersonalizationTagsEpic = (action$, { getState }) =>
  action$
    .ofType(actions.fetchPersonalizationTags.toString())
    .pluck('payload')
    .filter(() => !fetchPersonalizationTagsSuccess(getState()))
    .mergeMap(() => {
      return api
        .fetchPersonalizationTags()
        .map(response => actions.fetchPersonalizationTagsSuccess(response))
        .startWith(actions.fetchPersonalizationTagsPending())
        .catch(error => Observable.of(actions.fetchPersonalizationTagsFailed(error)))
    })

const sendTestEmailEpic = (action$) => action$
  .ofType(actions.sendTestEmail.toString())
  .pluck('payload')
  .mergeMap((taskType) => {
    return api
      .sendTestEmail(taskType)
      .map(response => {
        app.actions.NotificationActions.pushNotification({
          content: "Your test email has been sent!",
          variant: 'green',
        })
        return actions.sendTestEmailSuccess(response)
      })
      .startWith(actions.sendTestEmailPending())
      .catch(error => {
        
        app.actions.NotificationActions.pushNotification({
          content: "There was an error sending your test email, please try again.",
          variant: 'red',
        })
        return Observable.of(actions.sendTestEmailFailed(error))
      })
  })

export const epic = combineEpics(
  fetchAssistantOrgSettingsEpic,
  createAssistantOrgSettingsEpic,
  patchAssistantOrgSettingsEpic,
  fetchAssistantAgentSettingsEpic,
  createAssistantAgentSettingsEpic,
  patchAssistantAgentSettingsEpic,
  fetchSalesforceSettingsEpic,
  fetchPersonalizationTagsEpic,
  sendTestEmailEpic
)



// WEBPACK FOOTER //
// ./src/scripts/app/modules/assistantSettings/epic.js